import {Injectable} from '@angular/core';

/**
 * Options available when opening new modal windows with `DbModalService.open()` method.
 */
export interface DbModalOptions {
  /** If specified, modal opening and closing will be animated with this animation type.*/
  animation?: 'fade' | 'scale';

  /**
   * If `true`, the backdrop element will be created for a given modal.
   *
   * Alternatively, specify `'static'` for a backdrop which doesn't close the modal on click.
   *
   * Default value is `true`.
   */
  backdrop?: boolean | 'static';

  backdropClass?: string;

  /**
   * Callback right before the modal will be dismissed.
   *
   * If this function returns:
   * * `false`
   * * a promise resolved with `false`
   * * a promise that is rejected
   *
   * then the modal won't be dismissed.
   */
  beforeDismiss?: () => boolean | Promise<boolean>;

  position?: 'top' | 'center' | 'bottom';

  /** modal window height */
  height?: number | string;

  /** modal window width */
  width?: number;

  /** If `false` hide close button on top-right */
  hasCloseBtn?: boolean;

  /** If `false` hide close button on top-right */
  hasOutsideCloseBtn?: boolean;

  /** If `false` hide close button on top-left */
  hasOutsideLeftCloseBtn?: boolean;

  /** If `false` hide close button on top-right */
  hasOutsideRightCloseBtn?: boolean;

  fixMobile?: boolean;

  hasCloseSharpBtn?: boolean;

  /** If `true` make white close button on top-right */
  whiteCloseBtn?: boolean;

  /** modal window cover the screen */
  fullHeight?: boolean;

  /** modal window is overflow hidden */
  overflowHidden?: boolean;

  isPaidFeature?: boolean;
}

/**
 * A configuration service for the [`DbModalService`](#/components/modal/api#DbModalService) service.
 *
 * You can inject this service, typically in your root component, and customize the values of its properties in
 * order to provide default values for all modals used in the application.*/
@Injectable({providedIn: 'root'})
export class DbModalConfig implements Required<DbModalOptions> {
  animation: 'fade' | 'scale' = 'scale';
  backdrop: boolean | 'static' = true;
  backdropClass: string = '';
  beforeDismiss: () => boolean | Promise<boolean>;
  position: 'top' | 'center' | 'bottom' = 'center';
  height: number;
  width: number;
  hasCloseBtn: boolean = true;
  hasOutsideCloseBtn: boolean = false;
  hasOutsideLeftCloseBtn: boolean = false;
  hasOutsideRightCloseBtn: boolean = false;
  hasCloseSharpBtn: boolean = false;
  whiteCloseBtn: boolean = false;
  fullHeight: boolean = false;
  overflowHidden: boolean = true;
  isPaidFeature: boolean = false;
  fixMobile: boolean = false;

  private _animationEnabled: boolean = true;

  get animationEnabled(): boolean { return this._animationEnabled; }
  set animationEnabled(animation: boolean) { this._animationEnabled = animation; }
}
